import { Outlet, useRouteLoaderData } from 'react-router-dom';
import { MenuItems, Sidebar } from './components/sidebar';
import { LoginResponseDto } from './lib/api';
import { checkPermission, checkPermissionAny } from './utils/check-permissions';
import { Toaster } from '@/components/ui/toaster';
import SessionWarningModal from '@/shared/auth/session-warning-modal';
import { FeatureFlagProvider, useFeatureFlag } from './providers/feature-flag-provider';
import { useRouteTracking } from './hooks/useRouteTracking';
import { useComplianceBadge } from './musa/compliance/hooks/use-compliance-summary';

export function getMenuItems(
    session: LoginResponseDto,
    isEnabled: ReturnType<typeof useFeatureFlag>['isEnabled'],
    complianceBadge: number = 0,
): MenuItems {
    if (session.activeOrg?.id === 'admin') {
        return {
            users: {
                url: '/admin/users',
                label: 'Users',
            },
            organization: {
                url: '/admin/organizations',
                label: 'Organizations',
            },
            featureFlags: {
                url: '/admin/feature-flags',
                label: 'Feature Flags',
            },
            llmQuotas: {
                url: '/admin/llm-quotas',
                label: 'LLM Quotas',
            },
            auditLogs: {
                url: '/admin/audit-logs',
                label: 'Audit Logs',
            },
            enquiries: {
                url: '/admin/enquiries',
                label: 'Enquiries',
            },
        };
    }
    return {
        dashboard:
            (checkPermissionAny(session, ['admin-dashboard', 'qa-dashboard']) ||
                (checkPermission(session, 'clinical-dashboard') && isEnabled('clinical-dashboard'))) &&
            isEnabled('dashboard')
                ? {
                      url: '/dashboard',
                      label: 'Dashboard',
                  }
                : undefined,
        patients: checkPermission(session, 'patient')
            ? {
                  url: '/patients',
                  label: 'Patients',
              }
            : undefined,
        schedule: checkPermission(session, 'visit')
            ? {
                  url: '/schedule',
                  label: 'Schedule',
              }
            : undefined,
        callLog: checkPermission(session, 'call-log')
            ? {
                  url: '/call-log',
                  label: 'Call Log',
              }
            : undefined,
        qa: checkPermission(session, 'qa')
            ? {
                  url: '/qa',
                  label: 'QA',
              }
            : undefined,
        orders: checkPermission(session, 'orders')
            ? {
                  url: '/orders',
                  label: 'Orders',
              }
            : undefined,
        cms: checkPermission(session, 'cms')
            ? {
                  url: '/cms',
                  label: 'CMS',
              }
            : undefined,
        billing: checkPermission(session, 'billing')
            ? {
                  url: '/billing',
                  label: 'Billing',
              }
            : undefined,
        payroll: checkPermission(session, 'payroll')
            ? {
                  url: '/payroll',
                  label: 'Payroll',
              }
            : undefined,
        report:
            isEnabled('report-feature-flag') && checkPermission(session, 'report')
                ? {
                      url: '/report',
                      label: 'Reports',
                  }
                : undefined,
        staff: session.scope?.organization
            ? {
                  url: '/compliance',
                  label: 'Staff',
                  badge: complianceBadge > 0 ? complianceBadge : undefined,
              }
            : undefined,
        organization: checkPermission(session)
            ? {
                  url: '/organization/overview',
                  label: 'Organization',
              }
            : undefined,
        settings: checkPermission(session)
            ? {
                  url: '/settings',
                  label: 'Settings',
              }
            : undefined,
    };
}

function AppContent() {
    const session = useRouteLoaderData('root') as LoginResponseDto;
    const { isEnabled } = useFeatureFlag();
    const complianceBadge = useComplianceBadge(session);

    useRouteTracking(!!session?.activeOrg && session.activeOrg.id !== 'admin');

    return (
        <Sidebar menuItems={getMenuItems(session, isEnabled, complianceBadge)}>
            <Outlet />
            <Toaster />
            <SessionWarningModal />
        </Sidebar>
    );
}

export function App() {
    return (
        <FeatureFlagProvider>
            <AppContent />
        </FeatureFlagProvider>
    );
}
